import type { BuxusOperatorPayload } from "../payload/types";
import { getAt } from "../payload/getAt";
import {
  countyStateFromLocationLikeRow,
  jurisdictionCountyStateKey,
} from "../client/jurisdictionKeys";
import { locationRowMatchesJurisdictionKeys } from "../client/jurisdictionCountyMatch";

/** Unfiltered array length on `operator` (legacy menu uses raw counts, not stakeholder-filtered body). */
export function operatorArrayLen(operator: Record<string, unknown>, key: string): number {
  const v = operator[key];
  return Array.isArray(v) ? v.length : 0;
}

function operatorHasStakeholderVisibleRows(
  operator: Record<string, unknown>,
  key: string,
  stakeholderCode: string | null,
): boolean {
  const v = operator[key];
  if (!Array.isArray(v) || v.length === 0) return false;
  if (stakeholderCode == null || stakeholderCode === "") return v.length > 0;
  for (const row of v) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const stakeholders = r.stakeholders ?? r.Stakeholders;
    if (!Array.isArray(stakeholders) || stakeholders.length === 0) return true;
    if (stakeholders.includes(stakeholderCode)) return true;
  }
  return false;
}

export function countResourceMenuRows(operator: Record<string, unknown>): { links: number; docs: number } {
  const resources = Array.isArray(operator.resources) ? operator.resources : [];
  const lookup = Array.isArray(operator.lookupResources) ? operator.lookupResources : [];
  const links = resources.filter((r) => {
    if (!r || typeof r !== "object") return false;
    const o = r as Record<string, unknown>;
    return !!(o.label ?? o.Label);
  }).length;
  const docs = lookup.filter((r) => {
    if (!r || typeof r !== "object") return false;
    const o = r as Record<string, unknown>;
    return !!(o.label ?? o.Label ?? o.title ?? o.Title);
  }).length;
  return { links, docs };
}

/** Legacy `checkHasResources`: show Resources when more than one row total (links + documents). */
export function checkHasResourcesMenu(operator: Record<string, unknown>): boolean {
  const { links, docs } = countResourceMenuRows(operator);
  return links + docs > 1;
}

/** Legacy `checkHasSafety` (OperatorOverviewContainer / SafetyOverview). */
export function checkHasSafety(operator: Record<string, unknown>): boolean {
  return !!(
    operatorArrayLen(operator, "reliabilitySubform") ||
    operatorArrayLen(operator, "preventionSubform") ||
    operatorArrayLen(operator, "constructionSubform") ||
    operatorArrayLen(operator, "maintenanceSubform") ||
    operatorArrayLen(operator, "securitySubform") ||
    operatorArrayLen(operator, "integrityManagementSubform") ||
    operatorArrayLen(operator, "damagePreventionOverview") ||
    operatorArrayLen(operator, "markerImages") ||
    operatorArrayLen(operator, "oneCall") ||
    operatorArrayLen(operator, "encroachment") ||
    operatorArrayLen(operator, "reportDamage") ||
    operatorArrayLen(operator, "seeSomethingSaySomething")
  );
}

export function checkHasSafetyMeasures(operator: Record<string, unknown>): boolean {
  return !!(
    operatorArrayLen(operator, "safetyOverviewSubform") ||
    operatorArrayLen(operator, "reliabilitySubform") ||
    operatorArrayLen(operator, "preventionSubform") ||
    operatorArrayLen(operator, "constructionSubform") ||
    operatorArrayLen(operator, "maintenanceSubform") ||
    operatorArrayLen(operator, "securitySubform") ||
    operatorArrayLen(operator, "integrityManagementSubform")
  );
}

export function checkHasSafetyResponsibilities(operator: Record<string, unknown>): boolean {
  return !!(
    operatorArrayLen(operator, "damagePreventionOverview") ||
    operatorArrayLen(operator, "markerImages") ||
    operatorArrayLen(operator, "oneCall") ||
    operatorArrayLen(operator, "encroachment") ||
    operatorArrayLen(operator, "reportDamage") ||
    operatorArrayLen(operator, "seeSomethingSaySomething")
  );
}

export function assetRowVisibleToStakeholder(
  asset: unknown,
  stakeholderCode: string | null,
): boolean {
  if (!asset || typeof asset !== "object") return false;
  const r = asset as Record<string, unknown>;
  const restrict = r.restrictToStakeholders ?? r.RestrictToStakeholders;
  if (stakeholderCode == null || stakeholderCode === "") return true;
  if (!Array.isArray(restrict) || restrict.length === 0) return true;
  return restrict.includes(stakeholderCode);
}

function locationRowsMatchJurisdictions(
  locs: unknown[],
  keys: ReadonlySet<string>,
): boolean {
  for (const loc of locs) {
    if (!loc || typeof loc !== "object") continue;
    const o = loc as Record<string, unknown>;
    const cs = countyStateFromLocationLikeRow(o);
    if (!cs) continue;
    if (locationRowMatchesJurisdictionKeys(cs.county, cs.state, keys)) return true;
  }
  return false;
}

/** When `keys` is non-empty, asset must have at least one location in the set (mobile download parity). */
/**
 * Legacy `DownloadScreen` contact filter: when the user has specific jurisdictions,
 * keep a contact if `areasServed` is empty or any served county/state is in the profile set.
 */
export function contactRowVisibleForJurisdictions(
  contact: unknown,
  keys: ReadonlySet<string> | null | undefined,
): boolean {
  if (keys == null || keys.size === 0) return true;
  if (!contact || typeof contact !== "object") return false;
  const row = contact as Record<string, unknown>;
  const areas = row.areasServed ?? row.AreasServed;
  if (!Array.isArray(areas) || areas.length === 0) return true;
  for (const loc of areas) {
    if (!loc || typeof loc !== "object") continue;
    const cs = countyStateFromLocationLikeRow(loc as Record<string, unknown>);
    if (!cs) continue;
    if (keys.has(jurisdictionCountyStateKey(cs.county, cs.state))) return true;
    if (locationRowMatchesJurisdictionKeys(cs.county, cs.state, keys)) return true;
  }
  return false;
}

export function assetRowVisibleInJurisdictions(asset: unknown, keys: ReadonlySet<string>): boolean {
  if (!asset || typeof asset !== "object") return false;
  const r = asset as Record<string, unknown>;
  const primary = r.assetLocations ?? r.AssetLocations;
  if (Array.isArray(primary) && primary.length > 0) {
    if (locationRowsMatchJurisdictions(primary, keys)) return true;
  }
  const fallback = r.counties ?? r.Counties;
  if (Array.isArray(fallback) && fallback.length > 0) {
    if (locationRowsMatchJurisdictions(fallback, keys)) return true;
  }
  return false;
}

export type AssetVisibilityContext = {
  stakeholderCode: string | null;
  /** When set and non-empty, only assets with a matching `assetLocations` row are kept. */
  jurisdictionCountyStateKeys?: ReadonlySet<string> | null;
  /**
   * Operator catalog row from `get-all-operators-with-version`. ERP rows are manifest-filtered
   * from its `files[]` ids (jurisdiction-aware server-side); web then requires catalog SAS + ext.
   */
  catalogOperatorRow?: Record<string, unknown> | null;
  /**
   * Snapshot of catalog `files[]` ids from the last successful catalog refresh for this user scope.
   * Used to build file lists before the in-memory catalog returns on hard reload.
   */
  persistedManifestFileIds?: ReadonlySet<string> | null;
  /**
   * When set, **Documents & Plans** requires this to return true in addition to payload metadata.
   * Mobile: payload + manifest (ERP only) + files on disk. Web: same payload/manifest rules, catalog SAS instead of disk.
   */
  hasLocalAssetDocuments?: (assetId: string) => boolean;
  /** Web: active asset for per-asset screen visibility (stakeholder switch / sidebar context). */
  activeAssetId?: string | null;
};

export function assetIdAndName(row: Record<string, unknown>): { id: string; label: string } | null {
  const id = row.id ?? row.Id;
  const name = row.assetName ?? row.AssetName;
  if (typeof id !== "string" || !id.trim()) return null;
  const label = typeof name === "string" && name.trim() ? name.trim() : id;
  return { id: id.trim(), label };
}

/**
 * Assets visible for this session: stakeholder row filter, then optional jurisdiction filter
 * (same shape legacy mobile uses post-download).
 */
export function getVisibleAssets(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
): Record<string, unknown>[] {
  const fromOperator = getAt(payload, "operator.assets");
  let raw: unknown;
  if (Array.isArray(fromOperator)) {
    /** Use operator.assets as authoritative whenever it is an array (including empty). */
    raw = fromOperator;
  } else {
    /** Legacy blobs: assets only at payload root before normalize. */
    const root = payload as unknown as Record<string, unknown>;
    raw = Array.isArray(root.assets) ? root.assets : [];
  }
  if (!Array.isArray(raw)) return [];
  let list = raw.filter((a) => assetRowVisibleToStakeholder(a, ctx.stakeholderCode));
  const jur = ctx.jurisdictionCountyStateKeys;
  if (jur && jur.size > 0) {
    list = list.filter((a) => assetRowVisibleInJurisdictions(a, jur));
  }
  const arr = list as Record<string, unknown>[];
  /** Legacy mobile asset lists sort A–Z by display name (`assetName`, else `id`). */
  arr.sort((rowA, rowB) => {
    const a = assetIdAndName(rowA);
    const b = assetIdAndName(rowB);
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true });
  });
  return arr;
}

/**
 * Legacy-style nav visibility: `null` = use default section-based resolver for this screen.
 */
export function legacyOperatorScreenGate(
  screenId: string,
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
): boolean | null {
  const op = payload.operator as Record<string, unknown>;

  switch (screenId) {
    case "screen.resources":
      return checkHasResourcesMenu(op);
    case "screen.sustainability":
      return operatorHasStakeholderVisibleRows(op, "sustainabilitySubform", ctx.stakeholderCode);
    case "screen.testYourKnowledge":
      return operatorArrayLen(op, "testYourKnowledge") > 0;
    case "screen.safetyOverview":
      return checkHasSafety(op);
    case "screen.ourSafetyMeasures":
      return checkHasSafetyMeasures(op);
    case "screen.yourSafetyOverview":
      return checkHasSafetyResponsibilities(op);
    case "screen.inAnEmergency":
    case "screen.assets":
      return getVisibleAssets(payload, ctx).length > 0;
    case "screen.lineMarkers":
      return operatorArrayLen(op, "markerImages") > 0;
    case "screen.oneCall":
      return operatorArrayLen(op, "oneCall") > 0;
    case "screen.encroachment":
      return operatorArrayLen(op, "encroachment") > 0;
    case "screen.reportDamage":
      return operatorHasStakeholderVisibleRows(op, "reportDamage", ctx.stakeholderCode);
    case "screen.seeSomething":
      return operatorHasStakeholderVisibleRows(
        op,
        "seeSomethingSaySomething",
        ctx.stakeholderCode,
      );
    case "screen.reliability":
      return operatorArrayLen(op, "reliabilitySubform") > 0;
    case "screen.preventionMeasures":
      return operatorArrayLen(op, "preventionSubform") > 0;
    case "screen.construction":
      return operatorArrayLen(op, "constructionSubform") > 0;
    case "screen.maintenance":
      return operatorArrayLen(op, "maintenanceSubform") > 0;
    case "screen.security":
      return operatorArrayLen(op, "securitySubform") > 0;
    case "screen.integrityManagement":
      return operatorArrayLen(op, "integrityManagementSubform") > 0;
    case "screen.assetOverview":
      return true;
    case "screen.assetProfile":
    case "screen.assetEmergencyResponse":
    case "screen.assetEmergencyReport":
    case "screen.assetEmergencySteps":
    case "screen.assetEmergencyResponder":
    case "screen.assetEmergencyKeyLocations":
    case "screen.assetEmergencyCommunications":
    case "screen.assetEmergencyFireSuppression":
    case "screen.assetEmergencyWorstCase":
    case "screen.assetLineMarkers":
    case "screen.assetProductsHazards":
    case "screen.assetHazard":
    case "screen.assetDocuments":
    case "screen.assetKeyLocations":
    case "screen.assetGeneralHazards":
    case "screen.assetSpecialNotes":
      /** Always keep per-asset routes in the nav graph; body resolves with `activeAssetId`. */
      return true;
    default:
      return null;
  }
}
